import type {
    AiConversationTranscript,
    AiFeedbackParams,
    AiFeedbackStatus,
    AiHandoffParams,
    AiHandoffStatus,
    AiInboxBulkAction,
    AiInboxBulkResult,
    AiInboxBulkSelection,
    AiInboxItem,
    AiInboxParams,
    AiInboxResponse,
    AiInboxSummary,
    AiSupportHandoff,
    AiUserFeedback,
    PaginatedResponse,
} from '@/types';
import type { AxiosInstance, AxiosResponse } from 'axios';

/**
 * The feedback queue and conversation inzage (MILO-501).
 *
 * Separate from AiStatsService on purpose: those are read-only aggregates from
 * the gateway, these are milo-api's own records with a follow-up state you act
 * on. The transcript goes through milo-api too, never straight to the gateway:
 * the browser holds no shared secret and must not, and milo-api is where the
 * user's "support may read this" is checked and the read is audited.
 */
class SupportService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    inbox(params: AiInboxParams = {}): Promise<AxiosResponse<AiInboxResponse>> {
        return this.apiService.get('/api/admin/ai/inbox', { params });
    }

    bulkInbox(
        action: AiInboxBulkAction,
        items: AiInboxBulkSelection[],
    ): Promise<AxiosResponse<{ data: AiInboxBulkResult }>> {
        return this.apiService.post(
            '/api/admin/ai/inbox/bulk',
            { action, items },
            {
                // An all-failed batch has the same structured result as a partial batch.
                validateStatus: (status) => (status >= 200 && status < 300) || status === 400,
            },
        );
    }

    inboxSummary(): Promise<AxiosResponse<{ data: AiInboxSummary }>> {
        return this.apiService.get('/api/admin/ai/inbox/summary');
    }

    inboxItem(type: AiInboxItem['type'], id: string): Promise<AxiosResponse<{ data: AiInboxItem }>> {
        return this.apiService.get(`/api/admin/ai/inbox/${type}/${encodeURIComponent(id)}`);
    }

    index(params: AiFeedbackParams = {}): Promise<AxiosResponse<PaginatedResponse<AiUserFeedback[]>>> {
        return this.apiService.get('/api/admin/ai/feedback', { params });
    }

    updateStatus(id: string, status: AiFeedbackStatus): Promise<AxiosResponse<{ data: AiUserFeedback }>> {
        return this.apiService.patch(`/api/admin/ai/feedback/${encodeURIComponent(id)}`, { status });
    }

    handoffs(params: AiHandoffParams = {}): Promise<AxiosResponse<PaginatedResponse<AiSupportHandoff[]>>> {
        return this.apiService.get('/api/admin/ai/handoffs', { params });
    }

    updateHandoffStatus(id: string, status: AiHandoffStatus): Promise<AxiosResponse<{ data: AiSupportHandoff }>> {
        return this.apiService.patch(`/api/admin/ai/handoffs/${encodeURIComponent(id)}`, { status });
    }

    transcript(conversationId: string): Promise<AxiosResponse<AiConversationTranscript>> {
        return this.apiService.get(`/api/admin/ai/conversations/${encodeURIComponent(conversationId)}`);
    }
}

export default SupportService;
